
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/survey/LanguageSelector";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];

// RTL languages
const RTL_LANGUAGES = ["ar", "fa", "prs"];

const SurveyComplete = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { token, language: initialLanguage } = (location.state as { 
      token: string;
      language?: SupportedLanguage;
    }) || { token: "", language: undefined };
  
  const [language, setLanguage] = useState<SupportedLanguage>(initialLanguage || "sv");
  
  const isRtl = RTL_LANGUAGES.includes(language);
  
  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [token, navigate]);
  
  const getTitle = () => {
    const titleMap: Record<string, string> = {
      sv: "Tack för din medverkan!",
      en: "Thank you for your participation!",
      ar: "شكرا لمشاركتك!",
      ti: "ንምትሕብብርኩም ነመስግን!",
      fa: "از مشارکت شما متشکریم!",
      prs: "از اشتراک شما تشکر!",
      so: "Waad ku mahadsan tahay ka qayb-galkaaga!",
      uk: "Дякуємо за вашу участь!"
    };
    return titleMap[language] || titleMap.en;
  };
  
  const getWaitingText = () => {
    const waitingTextMap: Record<string, string> = {
      sv: "Du har svarat på enkäten. Din gruppledare kommer säga till när det är dags att svara på enkäten igen.",
      en: "You have answered the survey. Your group leader will tell you when it's time to answer the survey again.",
      ar: "لقد أجبت على الاستطلاع. سيخبرك قائد المجموعة عندما يحين وقت الإجابة على الاستطلاع مرة أخرى.",
      ti: "ነዛ መጽናዕቲ መሊስካዮ እኻ። ጉጅለ መራሒኻ መዓስ እንደገና ክትመልስ ከምዘለካ ክነግረካ እዩ።",
      fa: "شما به نظرسنجی پاسخ داده‌اید. رهبر گروه شما به شما خواهد گفت که چه زمانی باید دوباره به نظرسنجی پاسخ دهید.",
      prs: "شما به نظرسنجی جواب داده‌اید. رهبر گروه شما به شما خواهد گفت که چه وقت باید دوباره به نظرسنجی جواب دهید.",
      so: "Waxaad uga jawabtay sahaminta. Hogaamiyaha kooxdaada ayaa kuu sheegi doona goorta ay tahay in aad mar kale uga jawaabto sahaminta.",
      uk: "Ви відповіли на опитування. Лідер вашої групи повідомить вам, коли настане час відповідати на опитування знову."
    };
    return waitingTextMap[language] || waitingTextMap.en;
  };

  const getContinueText = () => {
    const continueTextMap: Record<string, string> = {
      sv: "Gå till startsidan",
      en: "Go to home page",
      ar: "الذهاب إلى الصفحة الرئيسية",
      ti: "ናብ ገጽ ቤት ኪድ",
      fa: "رفتن به صفحه اصلی",
      prs: "رفتن به صفحه اصلی",
      so: "Aad bogga hore",
      uk: "Перейти на головну сторінку"
    };
    return continueTextMap[language] || continueTextMap.en;
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <Card className="w-full max-w-md p-6 space-y-6 animate-fadeIn">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {getTitle()}
          </h1>
          <LanguageSelector
            selectedLanguage={language}
            onLanguageChange={setLanguage}
          />
        </div>
        
        <div className="space-y-4">
          <p className="text-center text-muted-foreground font-medium">
            {getWaitingText()}
          </p>
        </div>
        
        <div className="flex justify-center">
          <Button onClick={() => navigate("/")}>
            {getContinueText()}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SurveyComplete;
